import React, { useState } from "react";
import useApi from "@action.sustainability/storybook-dashboard/utils/fetching";
import Im from "immutable";
import { FormGroup, postFetch } from "../utils/forms";
import { Hide } from "./utils";
import { Modal, useModal } from "../modal";

const getCompanyMatches = ({ companyData, onSuccess }) => {
  postFetch(`/api/company/dispatch/`, {
    type: "GET_MATCHING",
    payload: companyData,
  }).then((data) => onSuccess(data.get("payload")));
};

const createCompany = ({ companyData, setPending, onSuccess }) => {
  setPending ? setPending(true) : null;
  console.log("createCompany", companyData);
  postFetch(`/api/company/`, companyData).then((data) => {
    console.log("Setup company succeeded", companyData);
    onSuccess();
  });
};

const requestJoinCompany = (companyId) => {
  postFetch(`/api/company/invite/`, {
    company: { uid: companyId },
    is_request: true,
  }).then((data) => {
    const success = !data?.get("errors");
    console.log("Company invite request returned data", success, data?.toJS());
    window.location.reload();
  });
};

const validateDomain = (text) => {
  if (!text) {
    return { error: true, msg: "Must provide a domain name" };
  }
  const re = /[!@#$%^&*()_+\=\[\]{};±'`:"\\|,<>\/?£]+/;
  if (!text.includes(".")) {
    return { error: true, msg: "Website entered has no dot (.com/.co.uk etc)" };
  }
  if (text.match(re)) {
    return {
      error: true,
      msg: "Website entered has a symbol or special character (@,£,%,!)",
    };
  }
  if (text.includes(" ")) {
    return { error: true, msg: "Website entered has a space" };
  }
  return { error: false, msg: "" };
};

const trimDomain = (text) => {
  text = text.trim().replace("http://", "").replace("https://", "");
  if (text.startsWith("www.")) {
    text = text.substring(4);
  }
  return text;
};

function FormInput({
  id,
  value,
  error,
  onChange,
  errorText,
  placeholder = null,
  type = "text",
}) {
  return (
    <div className="form-group">
      <input
        id={id}
        type={type}
        name={id}
        className={`form-control ${error ? "is-invalid" : ""}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <Hide when={!errorText}>
        <small id={`${id}Error`} className="form-text text-danger">
          {errorText}
        </small>
      </Hide>
    </div>
  );
}

function MultipleMatchPreamble() {
  return (
    <p>
      Please review the list below to see if your company account is already
      registered. If you find your company, select it, and we will notify your
      colleagues to confirm your association. This will allow you to join your
      company's existing account. If your company is not in the list, please
      click 'My company is not listed' to create a new company.
    </p>
  );
}

function ExactMatchPreamble() {
  return (
    <>
      <p>
        There is a company already on the system that exactly matches the name
        or domain given. This might be due to a colleague that has already
        registered and setup on the system.
      </p>
      <p>
        If this is your company then press the submit button below. We will send
        a request to your colleagues to verify you and accept you on to the
        system.
      </p>
      <p>
        If this is not your company, please contact{" "}
        <a
          className="text-primary"
          href="mailto: info@sustainabilitytool.com"
          target="_blank"
          rel="noreferrer"
        >
          info@sustainabilitytool.com
        </a>
        or select the Go back button.
      </p>
    </>
  );
}

function GoBackButton({ setMatchingCompanies }) {
  return (
    <>
      <button
        className="btn btn-danger float-left"
        onClick={(e) => setMatchingCompanies(null)}
      >
        Go back
      </button>
      <div style={{ clear: "both" }} />
    </>
  );
}

function ProceedToCreateButton({ setMatchingCompanies }) {
  return (
    <>
      <button
        className="btn btn-outline-secondary float-left"
        onClick={(e) => setMatchingCompanies(Im.List())}
      >
        My company is <u>not</u> listed
      </button>
      <div style={{ clear: "both" }} />
    </>
  );
}

function RequestJoinCompanyButton({ matchingCompanies, selected }) {
  const selectedCompanyId = matchingCompanies.get(selected)?.get("uid");
  return (
    <button
      className="btn btn-primary float-right"
      onClick={(e) => requestJoinCompany(selectedCompanyId)}
    >
      Join selected company
    </button>
  );
}

function SelectMatch(props) {
  const { name, domain, matchingCompanies } = props;
  console.log("SelectMatch", name, domain, matchingCompanies?.toJS());

  if (!matchingCompanies) {
    return null;
  }

  const [selected, setSelected] = useState(
    matchingCompanies.findIndex(
      (i) => i.get("name") == name || i.get("domain") == domain,
    ),
  );
  const [exactMatch, setExactMatch] = useState(false);

  const rows = matchingCompanies
    .filter((match, i) => (exactMatch ? i == selected : true))
    .map((match, i) => (
      <div key={i} className="form-check mt-1 mb-1">
        <input
          className="form-check-input"
          type="checkbox"
          checked={i == selected}
          id={match.get("uid")}
          onChange={(e) => {
            if (!exactMatch) {
              setSelected(i == selected ? -1 : i);
            }
          }}
        />
        <label className="form-check-label" htmlFor={match.get("uid")}>
          {match.get("name")}
        </label>
      </div>
    ));

  return (
    <div>
      <h4 className="mb-3">Potential company match found</h4>
      {exactMatch ? <ExactMatchPreamble /> : <MultipleMatchPreamble />}
      <hr />
      {rows}
      <hr />
      {selected > -1 ? (
        <RequestJoinCompanyButton
          matchingCompanies={matchingCompanies}
          selected={selected}
        />
      ) : null}
      {!exactMatch ? (
        <ProceedToCreateButton {...props} />
      ) : (
        <GoBackButton {...props} />
      )}
    </div>
  );
}

function SetupNewCompany(props) {
  const { name, domain, setMatchingCompanies } = props;

  if (!name) {
    setMatchingCompanies(null);
  }

  return (
    <>
      <h4 className="pb-2">Final step</h4>
      <p className="lead pt-3">
        You are setting up a new company account for {name} (domain: {domain}).
        To finalise your account creation, please click 'Continue' below."
      </p>
      <div className="text-center">
        <button
          className="btn btn-primary float-right"
          onClick={(e) =>
            createCompany({
              companyData: { name, domain },
              onSuccess: () => location.reload(),
            })
          }
        >
          Continue
        </button>
        <GoBackButton {...props} />
      </div>
    </>
  );
}

export function SetupCompanyForm() {
  const [companyData, setCompanyData] = useState({ name: "", domain: "" });
  const { data, isLoading } = useApi("/api/company/invite/");

  const [domainValidation, setDomainValidation] = useState({
    error: true,
    msg: "Please provide a domain name",
  });
  const [matchingCompanies, setMatchingCompanies] = useState(null);
  const [pending, setPending] = useState(false);
  console.log(
    "SetupCompanyForm",
    domainValidation,
    companyData,
    matchingCompanies?.toJS(),
  );

  const validateAndSetCompanyDomain = (value) => {
    const _value = trimDomain(value);
    setDomainValidation(validateDomain(_value));
    setCompanyData({ ...companyData, domain: _value });
  };

  const onMatchSuccess = (data) => setMatchingCompanies(data);

  // If we have some matches then the match-request has been submitted and returned
  if (matchingCompanies) {
    const NextComponent = matchingCompanies?.size
      ? SelectMatch
      : SetupNewCompany;
    return (
      <NextComponent
        {...{ ...companyData, matchingCompanies, setMatchingCompanies }}
      />
    );
  }

  // The form below submits a request to find a match
  return (
    <>
      <h4>Welcome to the Product Carbon Calculator!</h4>
      <p>
        To get you started, please share your company name and domain name.
        We'll look for any matching accounts and offer you the chance to join an
        existing company account if one is found.
      </p>
      <div className="form">
        <FormInput
          id="name"
          placeholder="Company name"
          value={companyData.name}
          onChange={(v) => setCompanyData({ ...companyData, name: v })}
        />
        <FormInput
          id="domain"
          placeholder="Company domain"
          value={companyData.domain}
          errorText={domainValidation.error ? domainValidation.msg : null}
          onChange={(v) => validateAndSetCompanyDomain(v)}
        />
        <button
          className="btn btn-sm btn-outline-primary"
          disabled={domainValidation?.error || pending}
          onClick={(e) =>
            getCompanyMatches({ companyData, onSuccess: onMatchSuccess })
          }
        >
          Create
        </button>
      </div>
    </>
  );
}

export function SetupCompanyModal() {
  const { trigger } = useApi(`/api/company/`);
  const { hideModal } = useModal();
  return (
    <Modal title="Create a company">
      <SetupCompanyForm
        onSuccess={() => {
          trigger();
          hideModal();
        }}
      />
    </Modal>
  );
}
